// Design token màu (UI_SPEC §5): nguồn DUY NHẤT cho cả CSS lẫn Pixi. CSS nhận bảng này qua
// `injectColorTokens()` lúc boot; Pixi đọc số hex ở đây, không chép lại.
import { TABS } from '@data/screens';

/** Màu gốc (§5.1). */
export const BASE_COLORS = {
  'c-bg': '#0a100e',
  'c-bg-deep': '#070a09',
  'c-ink': '#09100e',
  'c-paper': '#efe2bd',
  'c-gold': '#d8bc7a',
  'c-jade': '#72d7b2',
  'c-text': '#f1e5c5',
  'c-text-2': 'rgba(236,228,206,.72)',
  'c-text-3': 'rgba(232,222,198,.5)',
  'c-line': 'rgba(226,209,169,.12)',
  'c-panel': 'rgba(231,218,184,.03)',
  'c-num-hero': '#f2c14e',
  'c-righteous': '#7fc8e9',
  'c-evil': '#e0738a',
} as const;

/** Màu trạng thái (§5.2), dạng "r,g,b". `--tt-dat` = tin, `--tt-nguy` = việc. */
export const STATE_RGB = {
  viec: '224,86,74',
  tin: '110,231,200',
  chinh: '240,209,140',
  chet: '235,225,200',
  do: '232,160,106',
  dat: '110,231,200',
  nguy: '224,86,74',
} as const;

/** 7 phẩm cấp (§5.4): id theo lore, `lv` 0–6. */
export const QUALITIES = [
  { id: 'pham', rgb: '207,214,216' },
  { id: 'hoang', rgb: '127,206,143' },
  { id: 'huyen', rgb: '111,182,238' },
  { id: 'dia', rgb: '181,155,233' },
  { id: 'thien', rgb: '226,189,108' },
  { id: 'tien', rgb: '226,117,95' },
  { id: 'hondon', rgb: '127,224,208' },
] as const;

/** 7 nguyên tố (§5.5). */
export const ELEMENTS = [
  { id: 'kim', rgb: '232,201,106' },
  { id: 'moc', rgb: '127,206,143' },
  { id: 'thuy', rgb: '111,182,238' },
  { id: 'hoa', rgb: '226,117,95' },
  { id: 'tho', rgb: '195,154,107' },
  { id: 'quang', rgb: '242,240,230' },
  { id: 'am', rgb: '154,163,168' },
] as const;

/** Nhiễm, kiếp, telegraph (§5.6). */
export const FX_COLORS = {
  'nh-loi': '#b9a6ff',
  'nh-ma': '#b0304a',
  'nh-hu': '#8e9a9c',
  'tel-nguy': '#ff4060',
  'tel-song': '#ffd35a',
  'tel-doc': '#c08cff',
  'kiep-loi': '#e8e0ff',
  'kiep-huyen': '#f0c8d8',
  'kiep-tamma': '#6a1020',
} as const;

/** Tông tiền tệ `--cur-rgb` (§5.7), khóa theo id GLOSSARY. */
export const CURRENCY_RGB = {
  spiritStone: '226,189,108',
  summonTalisman: '240,180,94',
  dust: '169,168,159',
  spiritShard: '127,196,240',
  refiningStone: '198,206,208',
  sectMerit: '226,142,112',
  soulShard: '181,155,233',
  ashEssence: '154,163,168',
  realmPass: '131,191,240',
  lampFlame: '255,190,110',
} as const;

const toHex = (rgb: string): string =>
  '#' +
  rgb
    .split(',')
    .map((x) => Number(x).toString(16).padStart(2, '0'))
    .join('');

/** Số hex cho Pixi (0xRRGGBB). */
export function hexNum(color: string): number {
  const h = color.startsWith('#') ? color : toHex(color);
  return parseInt(h.slice(1, 7), 16);
}

export const PIXI_COLORS = {
  bgDeep: hexNum(BASE_COLORS['c-bg-deep']),
  jade: hexNum(BASE_COLORS['c-jade']),
  gold: hexNum(BASE_COLORS['c-gold']),
  paper: hexNum(BASE_COLORS['c-paper']),
  numHero: hexNum(BASE_COLORS['c-num-hero']),
} as const;

/** Toàn bộ biến màu dạng CSS (`--tên: giá trị;`). */
export function colorTokensCss(): string {
  const lines: string[] = [];
  for (const [k, v] of Object.entries(BASE_COLORS)) lines.push(`--${k}:${v};`);
  for (const [k, rgb] of Object.entries(STATE_RGB))
    lines.push(`--tt-${k}-rgb:${rgb};`, `--tt-${k}:rgb(${rgb});`);
  QUALITIES.forEach((q) => lines.push(`--q-${q.id}-rgb:${q.rgb};`, `--q-${q.id}:${toHex(q.rgb)};`));
  for (const e of ELEMENTS) lines.push(`--el-${e.id}-rgb:${e.rgb};`, `--el-${e.id}:${toHex(e.rgb)};`);
  for (const [k, v] of Object.entries(FX_COLORS)) lines.push(`--${k}:${v};`);
  for (const [k, rgb] of Object.entries(CURRENCY_RGB)) lines.push(`--cur-${k}-rgb:${rgb};`);
  for (const t of TABS) lines.push(`--accent-${t.id}-rgb:${t.accentRgb};`);
  const rules = [`:root{${lines.join('')}}`];
  // Class phẩm: đặt --q, --q-rgb, --q-lv (§5.4).
  QUALITIES.forEach((q, lv) => rules.push(`.q-${q.id}{--q:${toHex(q.rgb)};--q-rgb:${q.rgb};--q-lv:${lv};}`));
  // Accent theo tab: đặt trên .app mỗi lần đổi tab (§5.3).
  for (const t of TABS)
    rules.push(`.app[data-tab="${t.id}"]{--accent-rgb:${t.accentRgb};--accent:${toHex(t.accentRgb)};}`);
  return rules.join('\n');
}

export function injectColorTokens(doc: Document = document): void {
  if (doc.getElementById('ht-color-tokens')) return;
  const el = doc.createElement('style');
  el.id = 'ht-color-tokens';
  el.textContent = colorTokensCss();
  doc.head.appendChild(el);
}
